// Libraries
import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  Renderer,
  forwardRef,
  Inject,
  ViewChild
} from '@angular/core';
import {
  DatePipe
} from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import {
  MYDate,
  MYOptions,
  MYDateModel
} from './interfaces/common';
import {
  PageScrollService,
  PageScrollInstance
} from 'ng2-page-scroll';
import { DOCUMENT } from '@angular/platform-browser';
import * as lodash from 'lodash';

/**
 * Enums
 */
enum Year {
  min = 1100,
  max = 9100
}
/**
 * `MonthYearPickerComponent` implements a month and year picker
 */
@Component({
  selector: 'wp-month-year-picker',
  styles: [`
    .wp-month-year-picker-wrapper {
      position: relative;
    }
    .wp-month-year-picker-wrapper .wp-month-year-picker-container {
      box-sizing: border-box;
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2), 0 1px 1px 0 rgba(0, 0, 0, 0.14), 0 2px 1px -1px rgba(0, 0, 0, 0.12);
      background-color: white;
      color: black;
      font-size: 12px;
      height: 308px;
      margin: 0;
      padding: 0;
      position: absolute;
      z-index: 4;
    }
    .wp-month-year-picker-wrapper .wp-month-year-picker-table {
      display: block;
      height: 100%;
      overflow-y: scroll;
      overflow-x: hidden;
    }
    .wp-month-year-picker-wrapper .input-group-addon {
      cursor: pointer;
    }
    .wp-month-year-picker-wrapper .input-group-addon-second {
      border-left: none;
      border-right: none;
    }
    .wp-month-year-picker-wrapper .input-group-addon-disabled {
      color: #BAB8B8;
      cursor: default;
    }
    .wp-month-year-picker-wrapper .wp-month-year-picker-body {
      border-bottom: 1px solid #eeeeee;
      border-top: 1px solid #eeeeee;
    }
    .wp-month-year-picker-wrapper .wp-month-year-picker-body:first-child {
      border-top: none;
    }
    .wp-month-year-picker-wrapper .wp-month-year-picker-body:last-child {
      border-bottom: none;
    }
    .wp-month-year-picker-wrapper .wp-month-year-picker-cell {
      height: 44px;
    }
    .wp-month-year-picker-wrapper .wp-month-year-picker-cell-year {
      padding: 0 0 0 24px;
      font-weight: 500;
      font-size: 14px;
    }
    .wp-month-year-picker-wrapper .wp-month-year-picker-cell-month {
      box-sizing: content-box;
      line-height: 40px;
      text-align: center;
      vertical-align: middle;
      width: 44px;
    }
    .wp-month-year-picker-wrapper .wp-month-year-picker-cell-month .wp-month-year-picker-month-text {
      border-radius: 50%;
      cursor: pointer;
      display: inline-block;
      height: 40px;
      text-align: center;
      width: 40px;
    }
    .wp-month-year-picker-wrapper .wp-month-year-picker-cell-month
    .wp-month-year-picker-month-text.selected, 
    .wp-month-year-picker-wrapper .wp-month-year-picker-cell-month .wp-month-year-picker-month-text.selected:hover {
      color: rgba(255, 255, 255, 0.87);
      background-color: #106CC8;
    }
    .wp-month-year-picker-wrapper .wp-month-year-picker-cell-month
    .wp-month-year-picker-month-text.disabled, .wp-month-year-picker-wrapper .wp-month-year-picker-cell-month
    .wp-month-year-picker-month-text.disabled:hover {
      background-color: transparent;
      color: rgba(0, 0, 0, 0.435);
      cursor: default;
    }
    .wp-month-year-picker-wrapper .wp-month-year-picker-cell-month .wp-month-year-picker-month-text:hover {
      background-color: #e0e0e0;
    }
    .wp-month-year-picker-wrapper .wp-month-year-picker-cell-month:focus,
    .wp-month-year-picker-wrapper .wp-month-year-picker-month-text:focus {
      outline: none;
    }
  `],
  template: `
    <div class="wp-month-year-picker-wrapper">
      <!-- Input field -->
      <div class="input-group">
        <input #dateInput type="text"
        class="form-control" [ngModel]="selectedDate?.viewFormatted" name="selectedDate" (keydown)="onKeyDown($event)" required disabled>
        <span class="input-group-addon input-group-addon-second"
        (click)="prevMonth()" *ngIf="options.nextAndPrevButtonEnabled" [class.input-group-addon-disabled]="isPrevMonthDisabled()">
          <i class="fa fa-chevron-left"></i>
        </span>
        <span class="input-group-addon" (click)="toggle()" [class.input-group-addon-disabled]="disabled">
          <i class="fa fa-calendar"></i>
        </span>
        <span class="input-group-addon" (click)="nextMonth()"
        *ngIf="options.nextAndPrevButtonEnabled" [class.input-group-addon-disabled]="isNextMonthDisabled()">
          <i class="fa fa-chevron-right"></i>
        </span>
      </div>
      <!-- Date picker -->
      <div class="wp-month-year-picker-container" *ngIf="visible">
        <table role="grid" tabindex="0" class="wp-month-year-picker-table" aria-readonly="true" #MonthYearPickerTable>
          <tbody *ngFor="let year of years" class="wp-month-year-picker-body" id="wp-month-year-picker-body-{{year}}">
            <tr>
              <td class="wp-month-year-picker-cell wp-month-year-picker-cell-year">{{year}}</td>
              <!-- TODO: Create child component -->
              <td *ngFor="let month of months | slice:0:6" tabindex="-1"
              class="wp-month-year-picker-cell wp-month-year-picker-cell-month" role="gridcell">
                <span class="wp-month-year-picker-month-text"
                [ngClass]="{'selected': isCellSelected(month,year),'disabled': isCellDisabled(month,year)}" (click)=selectCell(month,year)>
                  {{month | wpMonthName}}
                </span>
              </td>
            </tr>
            <tr>
              <td tabindex="-1" role="gridcell"></td>
              <!-- TODO: Create child component -->
              <td *ngFor="let month of months | slice:6:12"
              tabindex="-1" class="wp-month-year-picker-cell wp-month-year-picker-cell-month" role="gridcell">
                <span class="wp-month-year-picker-month-text"
                [ngClass]="{'selected': isCellSelected(month,year),'disabled': isCellDisabled(month,year)}" (click)=selectCell(month,year)>
                  {{month | wpMonthName}}
                </span>
              </td>
            </tr>
        </tbody>
        </table>
      </div>
    </div>
  `
})
export class MonthYearPickerComponent implements OnInit, OnDestroy, ControlValueAccessor {
  /**
   * Month year picker options
   */
  @Input() options: MYOptions = {};
  /**
   * Selected date
   */
  @Input() selectedDate: MYDateModel;
  /**
   * Store if is disabled or not
   */
  @Input() disabled = false;
  /**
   * Selected date change event
   */
  @Output() selectedDateChange: EventEmitter<MYDateModel>;
  /**
   * MonthPicker child
   */
  @ViewChild('MonthYearPickerTable')
  scrollContainer: any;

  // Public
  /**
   * Months
   */
  public months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  /**
   * Years
   */
  public years: number[] = [];
  /**
   * Picker visivility
   */
  public visible = false;

  // Private
  /**
   * Default options
   */
  private defaultOpts: MYOptions = {
    minYear: <number>this.getCurrentDate().getFullYear() - 5,
    maxYear: <number> this.getCurrentDate().getFullYear() + 5,
    dateFormat: <string> 'y-MM',
    dateViewFormat: <string> 'MM y',
    nextAndPrevButtonEnabled: <boolean> false,
    inverse: <boolean> true
  };
  /**
   * Global listener
   */
  private listener: any;
  /**
   * Propagate change function
   */
  private propagateChange: (_: any) => void = () => {};

  /**
   * Creates a new `MonthYearPickerComponent` instance.
   */
  constructor(private elementRef: ElementRef,
              private renderer: Renderer,
              private datePipe: DatePipe,
              private pageScrollService: PageScrollService,
              @Inject(DOCUMENT) private document: any) {
    // Create event emmiter
    this.selectedDateChange = new EventEmitter<MYDateModel>();
    // Listen document events
    this.listener = this.renderer.listenGlobal('document', 'click', (event: any) => this.handleClick(event));
  }

  /**
   * Implements on init method.
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   */
  ngOnInit() {
    // Set component options
    this.setOptions();
    // Generate array of years
    this.generateYears();
  }

  /**
   * Implements on destroy method
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   */
  ngOnDestroy() {
    // If there is a globa listener, call listening
    if (this.listener) {
      this.listener();
    }
  }

  /**
   * Implements toggle for default scroll and set visivility
   */
  toggle(): void {
    // Exit if is disabled
    if (this.disabled) {
      return;
    }
    // Set picker visivility
    this.visible = !this.visible;
    // Default scroll
    if (this.selectedDate) {
      this.scrollTo(this.selectedDate.date.year)
    };
  }

  /**
   * Select next month
   */
  nextMonth() {
    if (this.isNextMonthDisabled()) {
      return;
    }
    let date = new Date(this.selectedDate.jsdate.getTime());
    date.setMonth(date.getMonth() + 1);

    let myDate = {
      year: date.getFullYear(),
      month: date.getMonth() + 1
    };
    this.selectDate(myDate);
    // Hide popover
    this.visible = false;
  }

 /**
  * Select prev month
  */
  prevMonth() {
    if (this.isPrevMonthDisabled()) {
      return;
    }
    let date = new Date(this.selectedDate.jsdate.getTime());
    date.setMonth(date.getMonth() - 1);

    let myDate = {
      year: date.getFullYear(),
      month: date.getMonth() + 1
    };
    this.selectDate(myDate);
    // Hide popover
    this.visible = false;
  }

  /**
   * Select cell
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   */
  selectCell(month: number, year: number): void {
    let date: MYDate = { month: month, year: year };
    this.selectDate(date);
    // Hide date picker
    this.visible = false;
  }

  /**
   * Implements writeValue for ControlValueAccessor interface, this takes the value from model and updates it on the view
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   */
  writeValue(newDate: MYDateModel): void {
    if (newDate && newDate.jsdate) {
      let date: MYDate = { month: newDate.jsdate.getMonth() + 1, year: newDate.jsdate.getFullYear() };
      this.selectDate(date);
    }
  }

  /**
   * Implements setDisabledState for ControlValueAccessor interface
   * @author Joaquin Volpe <joaquin.volpe@woopasoft.com>
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Implements registerOnChange for ControlValueAccessor interface, this updates our propagation function
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   */
  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  /**
   * Implements registerOnTouched for ControlValueAccessor interface, this updates our propagation function
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   */
  registerOnTouched() {}

  /**
   * Returns if a month is selected for a given year
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   */
  isCellSelected(month: number, year: number): boolean {
    return this.selectedDate &&
           this.selectedDate.date.month === month &&
           this.selectedDate.date.year === year;
  }

  /**
   * Returns if a month is disabled for a given year
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   */
  isCellDisabled(month: number, year: number): boolean {
    return this.checkIfCellIsDisabled(month, year);
  }

  /**
   * Handles user click event
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   */
  handleClick(event: any): void {
    // Get target element
    let clickedComponent = event.target;
    // Asume we are outside until we get the opposite
    let inside = false;
    // Navigating between its parent nodes checking is one of them is my component
    do {
      // If one of them is my component, I'm inside otherwise outside
      if (clickedComponent === this.elementRef.nativeElement) {
        inside = true;
      }
      clickedComponent = clickedComponent.parentNode;
    } while (clickedComponent);
    if (!inside) {
      // Outside
      // The month year picke must be hidden
      this.visible = false;
    }
  }

  /**
   * On key down event
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   */
  onKeyDown(event: KeyboardEvent): void {
    // Prevent default behaviour for all the keys
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Disable next button
   */
  isNextMonthDisabled(): boolean {
    // Exit if selected date is not set
    if (!this.selectedDate) {
      return true;
    }
    let  month = this.selectedDate.date.month + 1;
    let  year = this.selectedDate.date.year;
    return this.disabled || this.checkIfCellIsDisabled(month, year);
  }

  /**
   * Disable back button
   */
  isPrevMonthDisabled(): boolean {
    // Exit if selected date is not set
    if (!this.selectedDate) {
      return true;
    }
    let  month = this.selectedDate.date.month - 1;
    let  year = this.selectedDate.date.year;
    return this.disabled || this.checkIfCellIsDisabled(month, year);
  }

  // Private
  private selectDate(date: MYDate): void {
    // If it is disabled we cannot selected
    if (this.checkIfCellIsDisabled(date.month, date.year)) {
      return;
    }
    this.selectedDate = this.getDateModel(date);
    // Emit for 2-way binding
    this.selectedDateChange.emit(this.selectedDate);
    // Propagate change for form control
    setTimeout(() => {
      this.propagateChange(this.selectedDate);
    });
  }

  private checkIfCellIsDisabled(month: number, year: number): boolean {
    return (this.isDisabledUntil(month, year) || this.isDisabledSince(month, year));
  }

  /**
   * Set options for the Month Year picker
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   */
  private setOptions(): void {
    // For each user options copy on the interanl opts object
    if (this.options) {
      this.options = lodash.assign(this.defaultOpts, this.options);

      // If the min year exceeds min year
      if (this.options.minYear) {
        if (this.options.minYear < Year.min) {
          this.options.minYear = Year.min;
        } else if (!this.options.disableUntil) {
          this.options.disableUntil = {
            month: 1,
            year: this.options.minYear
          };
        }
      }
      // If the min year exceeds max year
      if (this.options.maxYear) {
        if (this.options.maxYear > Year.max) {
          this.options.maxYear = Year.max;
        } else if (!this.options.disableSince) {
          this.options.disableSince = {
            month: 1,
            year: this.options.maxYear
          };
        }
      }
    }
  }

  /**
   * Get current date
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   */
  private getCurrentDate(): Date {
    return new Date();
  }

  /**
   * Generate array of years
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   */
  private generateYears(): void {
    // Generate years
    if (this.options.minYear && this.options.maxYear) {
      // Check inverse flag
      if (this.options.inverse) {
        // Generate years
        for (let i = this.options.maxYear; i >= this.options.minYear; i--) {
          this.years.push(i);
        }
      } else {
        // Generate years
        for (let i = this.options.minYear; i <= this.options.maxYear; i++) {
          this.years.push(i);
        }
      }
    }
  }

  /**
   * Returns if the month for a given year is disabled until given date
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   */
  private isDisabledUntil(month: number, year: number): boolean {
    if (!this.options.disableUntil) {
      return false;
    }
    return this.options.disableUntil.month > 0 && this.options.disableUntil.year > 0 &&
           ((year < this.options.disableUntil.year) ||
           ((month < this.options.disableUntil.month) && ( year === this.options.disableUntil.year)));
  }

  /**
   * Returns if the month for a given year is disabled since given date
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   */
  private isDisabledSince(month: number, year: number): boolean {
    if (!this.options.disableSince) {
      return false;
    }
    return this.options.disableSince.month > 0 && this.options.disableSince.year > 0 &&
           ((year > this.options.disableSince.year) ||
           ((month > this.options.disableSince.month) && ( year === this.options.disableSince.year)));
  }

  /**
   * Get date model
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   */
  private getDateModel(date: MYDate): MYDateModel {
    let jsdate = new Date(date.year, date.month - 1, 1);
    let formatDate = this.formatDate(jsdate);
    return {
      date: date,
      jsdate: jsdate,
      formatted: formatDate,
      viewFormatted: this.formatViewDate(jsdate),
      epoc: Math.round(jsdate.getTime() / 1000.0)
    };
  }

  /**
   * Format dateModel
   * @author Leandro Diaz <leandro.diaz@woopasoft.com>
   */
  private formatDate(date: Date): string {
    if (!date) {
      return '';
    }
    return this.datePipe.transform(date, this.options.dateFormat) || '';
  }

  /**
   * Default scroll
   * @param year Year to scroll
   */
  private scrollTo(year: number) {
    // Check if there is year to scroll
    if (!year) {
      return ;
    }
    // Speed scroll
    const scrollSpeed = 50000;
    // Wait time
    const waitTime = 50;
    let self = this;

    setTimeout(function() {
      // A specification of the DOM element to scroll to
      let scrollTarget = '#wp-month-year-picker-body-' + year;
      let options = {
        // The document object of the current app
        document: self.document,
        // A specification of the DOM element to scroll to
        scrollTarget: scrollTarget,
        // Array of HTMLElements or the body object that should be manipulated while performing the scroll animation
        scrollingViews: [self.scrollContainer.nativeElement],
        // Maximum speed to be used for the scroll animation
        pageScrollSpeed: scrollSpeed
      };

      // Scrolling action
      let pageScrollInstance: PageScrollInstance = PageScrollInstance.newInstance(options);
      self.pageScrollService.start(pageScrollInstance);

    }, waitTime);
  }

  /**
   * View date
   */
  private formatViewDate(date: Date): string {
    if (!date) {
      return '';
    }
    if (this.options.dateViewPipe) {
      return this.options.dateViewPipe.transform(date);
    }
    return this.datePipe.transform(date, this.options.dateViewFormat) || '';
  }
}
